<script setup>
import { ref, computed, onMounted, nextTick } from 'vue'
import L from 'leaflet'
import { useRouterStore } from '@/store/router'
import { useEstudios } from '@/composables/useEstudios'
import { useNominatim } from '@/composables/useNominatim'
import { useMapTiles } from '@/composables/useMapTiles'
import { useGoogleMapsUrl } from '@/composables/useGoogleMapsUrl'
import { DEFAULT_CENTER } from '@/store/mapConstants'
import { usePublicTheme } from '@/composables/usePublicTheme'

const themeStore = usePublicTheme()
const route = useRouterStore()
const token = computed(() => route.params.token)
const {
  fetchPublicEstudios,
  submitPublicEstudio,
  updatePublicEstudio,
  deletePublicEstudioAdjunto,
  getAdjuntoUrl,
} = useEstudios()
const { reverseGeocode } = useNominatim()
const { resolveLocation } = useGoogleMapsUrl()

const loading = ref(true)
const errorMsg = ref(null)
const congregacionNombre = ref('')
const grupo = ref(null)
const estudios = ref([])
// Centro de alguno de los territorios asignados al grupo (lo calcula el servidor)
const mapCenter = ref(null)

// vista: 'lista' | 'form'
const view = ref('lista')
const editingId = ref(null)

// Los datos de quien completa el formulario (nombre y whatsapp) se guardan en
// sessionStorage para no tener que volver a escribirlos si carga varios estudios.
const PUBLICADOR_KEY = 'estudios-publico-nombre'
const WHATSAPP_KEY = 'estudios-publico-whatsapp'

const emptyForm = () => ({
  publicador: sessionStorage.getItem(PUBLICADOR_KEY) || '',
  whatsapp: sessionStorage.getItem(WHATSAPP_KEY) || '',
  estudiante: '',
  estudianteWhatsapp: '',
  direccion: '',
  lat: null,
  lng: null,
  fechaInicio: '',
  aQuienSePaso: '',
  observaciones: '',
})
const form = ref(emptyForm())
const saving = ref(false)
const error = ref(null)
const success = ref(false)

// ─── Adjuntos (fotos y documentos) ───────────────────────────────────────────
const MAX_ADJUNTOS = 6
const MAX_ADJUNTO_SIZE = 10 * 1024 * 1024 // 10MB
const ACCEPT_ADJUNTOS = 'image/*,.pdf,.doc,.docx,.xls,.xlsx'
// Adjuntos ya guardados del estudio que se está editando (se borran al toque)
const adjuntosExistentes = ref([])
// Archivos elegidos en esta sesión, se suben recién al guardar el formulario
const pendingFiles = ref([])
const adjuntoError = ref(null)
const eliminandoAdjuntoId = ref(null)

const isImagen = (mimeType) => (mimeType || '').startsWith('image/')

const formatBytes = (bytes) => {
  if (!bytes) return ''
  const kb = bytes / 1024
  return kb < 1024 ? `${kb.toFixed(0)} KB` : `${(kb / 1024).toFixed(1)} MB`
}

const onFilesSelected = (e) => {
  const nuevos = Array.from(e.target.files || [])
  e.target.value = ''
  adjuntoError.value = null
  const totalActual = adjuntosExistentes.value.length + pendingFiles.value.length
  const disponibles = MAX_ADJUNTOS - totalActual
  if (disponibles <= 0) {
    adjuntoError.value = `Como máximo se pueden adjuntar ${MAX_ADJUNTOS} archivos.`
    return
  }
  const aceptados = []
  for (const file of nuevos) {
    if (file.size > MAX_ADJUNTO_SIZE) {
      adjuntoError.value = `"${file.name}" pesa demasiado (máximo 10MB).`
      continue
    }
    if (aceptados.length >= disponibles) {
      adjuntoError.value = `Como máximo se pueden adjuntar ${MAX_ADJUNTOS} archivos.`
      break
    }
    aceptados.push(file)
  }
  pendingFiles.value = [...pendingFiles.value, ...aceptados]
}

const quitarPendingFile = (index) => {
  pendingFiles.value = pendingFiles.value.filter((_, i) => i !== index)
  adjuntoError.value = null
}

const eliminarAdjuntoExistente = async (adjunto) => {
  eliminandoAdjuntoId.value = adjunto.id
  try {
    const res = await deletePublicEstudioAdjunto(token.value, editingId.value, adjunto.id)
    if (!res.error) {
      adjuntosExistentes.value = adjuntosExistentes.value.filter((a) => a.id !== adjunto.id)
    }
  } finally {
    eliminandoAdjuntoId.value = null
  }
}

const recordarDatosPublicador = () => {
  sessionStorage.setItem(PUBLICADOR_KEY, form.value.publicador.trim())
  sessionStorage.setItem(WHATSAPP_KEY, form.value.whatsapp.trim())
}

const load = async () => {
  loading.value = true
  errorMsg.value = null
  if (!token.value) {
    errorMsg.value = 'Enlace inválido: falta el token.'
    loading.value = false
    return
  }
  const res = await fetchPublicEstudios(token.value)
  if (res.error) {
    errorMsg.value = 'Este enlace no es válido o fue eliminado.'
  } else {
    congregacionNombre.value = res.congregacion?.name || ''
    grupo.value = res.grupo
    estudios.value = res.estudios || []
    mapCenter.value = res.mapCenter || null
    document.title = congregacionNombre.value ? `Estudios` : 'Estudios'
  }
  loading.value = false
}

// ─── Mapa para marcar la ubicación del estudio ───────────────────────────────
const showMap = ref(false)
const mapDiv = ref(null)
let leafletMap = null
let mapMarker = null
const tiles = useMapTiles(() => leafletMap, { local: true })

const placeMarker = (latlng) => {
  if (mapMarker) {
    mapMarker.setLatLng(latlng)
  } else {
    mapMarker = L.circleMarker(latlng, { radius: 9, weight: 3, fillOpacity: 0.6 }).addTo(leafletMap)
  }
}

const destroyMap = () => {
  leafletMap?.remove()
  leafletMap = null
  mapMarker = null
  showMap.value = false
}

const toggleMap = async () => {
  if (showMap.value) {
    destroyMap()
    return
  }
  showMap.value = true
  await nextTick()
  const hasCoords = form.value.lat != null && form.value.lng != null
  // Abre en la marca existente, o en el territorio asignado al grupo, para guiarse mejor
  const start = hasCoords ? [form.value.lat, form.value.lng] : (mapCenter.value || DEFAULT_CENTER)
  leafletMap = L.map(mapDiv.value).setView(start, hasCoords ? 17 : (mapCenter.value ? 16 : 14))
  tiles.applyCurrentTiles(leafletMap)
  if (hasCoords) placeMarker({ lat: form.value.lat, lng: form.value.lng })
  leafletMap.on('click', async (e) => {
    form.value.lat = e.latlng.lat
    form.value.lng = e.latlng.lng
    placeMarker(e.latlng)
    try {
      const res = await reverseGeocode({ lat: e.latlng.lat, lng: e.latlng.lng })
      if (res?.display_name && !form.value.direccion) form.value.direccion = res.display_name
    } catch {
      // Sin geocodificación: las coordenadas ya quedaron marcadas igual.
    }
  })
  setTimeout(() => leafletMap?.invalidateSize(), 100)
}

// ─── Pegar link de Google Maps (alternativa a marcar en el mapa) ────────────
const linkTexto = ref('')
const linkError = ref(null)
const linkResolving = ref(false)

const usarLink = async () => {
  const texto = linkTexto.value.trim()
  if (!texto) return
  linkError.value = null
  linkResolving.value = true
  try {
    const loc = await resolveLocation(texto)
    if (!loc) {
      linkError.value = 'No pudimos reconocer una ubicación en ese texto.'
      return
    }
    form.value.lat = loc.lat
    form.value.lng = loc.lng
    if (showMap.value && leafletMap) {
      placeMarker(loc)
      leafletMap.setView([loc.lat, loc.lng], 17)
    } else {
      await toggleMap()
    }
    if (!form.value.direccion) {
      try {
        const res = await reverseGeocode(loc)
        if (res?.display_name) form.value.direccion = res.display_name
      } catch {
        // Sin geocodificación: las coordenadas ya quedaron marcadas igual.
      }
    }
  } finally {
    linkResolving.value = false
  }
}

const nuevoEstudio = () => {
  editingId.value = null
  form.value = emptyForm()
  error.value = null
  success.value = false
  adjuntosExistentes.value = []
  pendingFiles.value = []
  adjuntoError.value = null
  linkTexto.value = ''
  linkError.value = null
  view.value = 'form'
}

const editarEstudio = (estudio) => {
  editingId.value = estudio.id
  form.value = {
    publicador: estudio.publicador,
    whatsapp: estudio.whatsapp,
    estudiante: estudio.estudiante,
    estudianteWhatsapp: estudio.estudianteWhatsapp,
    direccion: estudio.direccion,
    lat: estudio.lat,
    lng: estudio.lng,
    fechaInicio: estudio.fechaInicio,
    aQuienSePaso: estudio.aQuienSePaso,
    observaciones: estudio.observaciones,
  }
  error.value = null
  success.value = false
  adjuntosExistentes.value = estudio.adjuntos || []
  pendingFiles.value = []
  adjuntoError.value = null
  linkTexto.value = ''
  linkError.value = null
  view.value = 'form'
}

const submit = async () => {
  if (!form.value.publicador.trim() && !form.value.estudiante.trim()) {
    error.value = 'Ingresá al menos el publicador o el estudiante.'
    return
  }
  saving.value = true
  error.value = null
  const res = editingId.value
    ? await updatePublicEstudio(token.value, editingId.value, form.value, pendingFiles.value)
    : await submitPublicEstudio(token.value, form.value, pendingFiles.value)
  saving.value = false
  if (res.error) {
    error.value = res.error
  } else {
    recordarDatosPublicador()
    destroyMap()
    pendingFiles.value = []
    success.value = true
    await load()
  }
}

const volverALista = () => {
  destroyMap()
  view.value = 'lista'
  success.value = false
  editingId.value = null
  form.value = emptyForm()
  adjuntosExistentes.value = []
  pendingFiles.value = []
  adjuntoError.value = null
}

const formatFecha = (f) => {
  if (!f) return ''
  const [y, m, d] = f.split('-')
  return y && m && d ? `${d}/${m}/${y}` : f
}

onMounted(load)
</script>

<template>
  <div class="simple-page">
    <button
      type="button"
      class="theme-toggle-btn"
      :title="themeStore.theme === 'dark' ? 'Modo claro' : 'Modo oscuro'"
      @click="themeStore.toggle()"
    >
      <i :class="themeStore.theme === 'dark' ? 'pi pi-sun' : 'pi pi-moon'" />
    </button>

    <img src="/territorios-icono.png" alt="Logo" class="simple-logo">

    <div v-if="loading" class="simple-loading">
      <i class="pi pi-spin pi-spinner" />
    </div>

    <div v-else-if="errorMsg" class="simple-error">{{ errorMsg }}</div>

    <template v-else>
      <h1 class="simple-title">Estudios{{ congregacionNombre ? ` — ${congregacionNombre}` : '' }}</h1>
      <div v-if="grupo" class="simple-subtitle">
        Grupo {{ grupo.codigo }}{{ grupo.encargado ? ` · ${grupo.encargado}` : '' }}
      </div>

      <!-- Lista de estudios del grupo -->
      <template v-if="view === 'lista'">
        <button class="simple-btn simple-btn-add" @click="nuevoEstudio">
          <i class="pi pi-plus" /> Agregar estudio
        </button>

        <div v-if="!estudios.length" class="simple-hint">
          Todavía no hay estudios cargados para este grupo. Usá el botón de arriba para agregar el primero.
        </div>

        <div v-else class="simple-list">
          <div v-for="e in estudios" :key="e.id" class="simple-item">
            <div class="simple-item-main">
              <div class="simple-item-title">{{ e.estudiante || 'Sin nombre' }}</div>
              <div v-if="e.estudianteWhatsapp" class="simple-item-row">
                <a :href="`https://wa.me/${String(e.estudianteWhatsapp).replace(/\D/g, '')}`" target="_blank" rel="noopener">
                  <i class="pi pi-whatsapp" /> {{ e.estudianteWhatsapp }}
                </a>
              </div>
              <div v-if="e.publicador" class="simple-item-row">
                Publicador: {{ e.publicador }}
                <a v-if="e.whatsapp" :href="`https://wa.me/${String(e.whatsapp).replace(/\D/g, '')}`" target="_blank" rel="noopener">
                  <i class="pi pi-whatsapp" /> {{ e.whatsapp }}
                </a>
              </div>
              <div v-if="e.direccion" class="simple-item-row">
                {{ e.direccion }}
                <a
                  v-if="e.lat && e.lng"
                  :href="`https://www.google.com/maps?q=${e.lat},${e.lng}`"
                  target="_blank"
                  rel="noopener"
                >ver mapa</a>
              </div>
              <div v-if="e.fechaInicio" class="simple-item-row">Inicio: {{ formatFecha(e.fechaInicio) }}</div>
              <div v-if="e.aQuienSePaso" class="simple-item-row">Se lo pasó a: {{ e.aQuienSePaso }}</div>
              <div v-if="e.observaciones" class="simple-item-row simple-item-obs">{{ e.observaciones }}</div>
              <div v-if="e.adjuntos?.length" class="simple-item-adjuntos">
                <a
                  v-for="a in e.adjuntos"
                  :key="a.id"
                  :href="getAdjuntoUrl(a.id)"
                  target="_blank"
                  rel="noopener"
                  class="simple-item-adjunto"
                  :title="a.originalName"
                >
                  <img v-if="isImagen(a.mimeType)" :src="getAdjuntoUrl(a.id)" alt="">
                  <i v-else class="pi pi-file" />
                </a>
              </div>
            </div>
            <button class="simple-edit-btn" @click="editarEstudio(e)">
              <i class="pi pi-pencil" /> Editar
            </button>
          </div>
        </div>
      </template>

      <!-- Formulario alta / edición -->
      <template v-else>
        <div v-if="success" class="simple-done">
          <i class="pi pi-check-circle" />
          <div>{{ editingId ? '¡Listo! El estudio fue actualizado.' : '¡Gracias! El estudio fue registrado.' }}</div>
          <button class="simple-btn simple-btn-secondary" @click="volverALista">Volver a la lista</button>
        </div>

        <form v-else class="simple-form" @submit.prevent="submit">
          <button type="button" class="simple-back" @click="volverALista">
            <i class="pi pi-arrow-left" /> Volver a la lista
          </button>

          <label class="simple-field-label" for="simple-publicador">Tu Nombre</label>
          <input
            id="simple-publicador"
            v-model="form.publicador"
            type="text"
            class="simple-input"
            placeholder="Tu Nombre"
          >

          <label class="simple-field-label mt" for="simple-whatsapp">Tu Whatsapp <span class="simple-optional">(opcional)</span></label>
          <div class="simple-help">Para poder contactarte si surge alguna duda con el estudio.</div>
          <input
            id="simple-whatsapp"
            v-model="form.whatsapp"
            type="tel"
            class="simple-input"
            placeholder="Tu Whatsapp"
          >

          <label class="simple-field-label mt" for="simple-estudiante">Estudiante</label>
          <input
            id="simple-estudiante"
            v-model="form.estudiante"
            type="text"
            class="simple-input"
            placeholder="Nombre del estudiante"
          >

          <label class="simple-field-label mt" for="simple-estudiante-whatsapp">Teléfono del estudiante <span class="simple-optional">(opcional)</span></label>
          <input
            id="simple-estudiante-whatsapp"
            v-model="form.estudianteWhatsapp"
            type="tel"
            class="simple-input"
            placeholder="+54 9 264 555 5555"
          >

          <div class="simple-gps-block mt">
            <div v-if="form.lat && form.lng" class="simple-gps-status">
              <i class="pi pi-check-circle" />
              Ubicación capturada —
              <a :href="`https://www.google.com/maps?q=${form.lat},${form.lng}`" target="_blank" rel="noopener">ver en el mapa</a>
            </div>

            <label class="simple-field-label" for="simple-maps-link">Pegar link de Google Maps <span class="simple-optional">(opcional)</span></label>
            <div class="simple-help">Compartí la ubicación desde Google Maps ("Compartir" → "Copiar enlace") y pegala acá.</div>
            <div class="simple-link-row">
              <input
                id="simple-maps-link"
                v-model="linkTexto"
                type="text"
                class="simple-input"
                placeholder="https://maps.app.goo.gl/..."
                @keyup.enter="usarLink"
              >
              <button type="button" class="simple-link-btn" :disabled="linkResolving || !linkTexto.trim()" @click="usarLink">
                <i class="pi" :class="linkResolving ? 'pi-spin pi-spinner' : 'pi-check'" />
              </button>
            </div>
            <div v-if="linkError" class="simple-error-msg">{{ linkError }}</div>

            <button type="button" class="simple-gps-btn simple-map-btn" @click="toggleMap">
              <i class="pi" :class="showMap ? 'pi-times' : 'pi-map'" />
              {{ showMap ? 'Ocultar el mapa' : 'Ubicar en el mapa' }}
            </button>
            <div v-show="showMap" ref="mapDiv" class="simple-map" />
            <div v-if="showMap" class="simple-help">Tocá el mapa para marcar dónde se realiza el estudio.</div>
          </div>

          <label class="simple-field-label mt" for="simple-direccion">Dirección o referencia <span class="simple-optional">(opcional)</span></label>
          <input
            id="simple-direccion"
            v-model="form.direccion"
            type="text"
            class="simple-input"
            placeholder="Dónde se realiza el estudio"
          >

          <label class="simple-field-label mt" for="simple-fecha">Fecha de inicio <span class="simple-optional">(opcional)</span></label>
          <input
            id="simple-fecha"
            v-model="form.fechaInicio"
            type="date"
            class="simple-input"
          >

          <label class="simple-field-label mt" for="simple-paso">A quién se lo pasó <span class="simple-optional">(opcional)</span></label>
          <input
            id="simple-paso"
            v-model="form.aQuienSePaso"
            type="text"
            class="simple-input"
            placeholder="Si el estudio se pasó a otro publicador"
          >

          <label class="simple-field-label mt" for="simple-obs">Observaciones <span class="simple-optional">(opcional)</span></label>
          <textarea
            id="simple-obs"
            v-model="form.observaciones"
            class="simple-input simple-textarea"
            rows="3"
            placeholder="Horarios, avances, detalles del estudio..."
          />

          <label class="simple-field-label mt">Fotos o documentos <span class="simple-optional">(opcional)</span></label>
          <div class="simple-help">Podés adjuntar hasta {{ MAX_ADJUNTOS }} archivos (imágenes o PDF/Word/Excel, máx. 10MB c/u).</div>

          <div v-if="adjuntosExistentes.length" class="simple-adjuntos-list">
            <div v-for="a in adjuntosExistentes" :key="a.id" class="simple-adjunto-item">
              <a :href="getAdjuntoUrl(a.id)" target="_blank" rel="noopener" class="simple-adjunto-link">
                <img v-if="isImagen(a.mimeType)" :src="getAdjuntoUrl(a.id)" class="simple-adjunto-thumb" alt="">
                <i v-else class="pi pi-file simple-adjunto-icon" />
                <span class="simple-adjunto-name">{{ a.originalName }}</span>
              </a>
              <button
                type="button"
                class="simple-adjunto-remove"
                :disabled="eliminandoAdjuntoId === a.id"
                @click="eliminarAdjuntoExistente(a)"
              >
                <i class="pi" :class="eliminandoAdjuntoId === a.id ? 'pi-spin pi-spinner' : 'pi-times'" />
              </button>
            </div>
          </div>

          <div v-if="pendingFiles.length" class="simple-adjuntos-list">
            <div v-for="(f, i) in pendingFiles" :key="`${f.name}-${i}`" class="simple-adjunto-item">
              <div class="simple-adjunto-link">
                <i class="pi" :class="f.type.startsWith('image/') ? 'pi-image' : 'pi-file'" />
                <span class="simple-adjunto-name">{{ f.name }} <span class="simple-optional">({{ formatBytes(f.size) }})</span></span>
              </div>
              <button type="button" class="simple-adjunto-remove" @click="quitarPendingFile(i)">
                <i class="pi pi-times" />
              </button>
            </div>
          </div>

          <label
            v-if="adjuntosExistentes.length + pendingFiles.length < MAX_ADJUNTOS"
            class="simple-file-btn"
          >
            <i class="pi pi-paperclip" /> Adjuntar archivos
            <input type="file" multiple :accept="ACCEPT_ADJUNTOS" class="simple-file-input" @change="onFilesSelected">
          </label>

          <div v-if="adjuntoError" class="simple-error-msg">{{ adjuntoError }}</div>

          <div v-if="error" class="simple-error-msg">{{ error }}</div>

          <button type="submit" class="simple-btn" :disabled="saving">
            {{ saving ? 'Enviando...' : (editingId ? 'Guardar cambios' : 'Registrar estudio') }}
          </button>
        </form>
      </template>
    </template>
  </div>
</template>

<style scoped>
.simple-page {
  position: relative;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 32px 20px 48px;
  background: var(--bg-grouped, #f2f2f7);
  text-align: center;
}

.theme-toggle-btn {
  position: fixed;
  top: max(env(safe-area-inset-top, 0px), 16px);
  right: 16px;
  z-index: 10;
  width: 38px;
  height: 38px;
  border-radius: 50%;
  border: 1.5px solid var(--separator, rgba(0, 0, 0, 0.15));
  background: var(--bg-primary, #fff);
  color: var(--label-secondary, #666);
  font-size: 16px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  -webkit-tap-highlight-color: transparent;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.theme-toggle-btn:active {
  opacity: 0.7;
}

.simple-logo {
  width: 112px;
  height: 112px;
  margin-bottom: 28px;
}

.simple-loading {
  font-size: 2rem;
  color: var(--accent, #007aff);
  margin-top: 40px;
}

.simple-error {
  font-size: 18px;
  color: var(--label-secondary, #666);
  margin-top: 20px;
  max-width: 420px;
}

.simple-title {
  font-size: 26px;
  font-weight: 700;
  color: var(--label-primary, #111);
  margin: 0 0 6px;
}

.simple-subtitle {
  font-size: 16px;
  color: var(--label-secondary, #666);
  margin-bottom: 20px;
}

.simple-hint {
  width: 100%;
  max-width: 420px;
  background: var(--info-soft, #e6f2ff);
  color: var(--label-primary, #333);
  border-radius: 12px;
  padding: 14px 16px;
  font-size: 14px;
  line-height: 1.4;
  margin-top: 16px;
  text-align: left;
}

.simple-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
  max-width: 420px;
  margin-top: 16px;
}

.simple-item {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  background: var(--bg-primary, #fff);
  border-radius: 14px;
  padding: 14px 16px;
  text-align: left;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
}

.simple-item-title {
  font-size: 17px;
  font-weight: 700;
  color: var(--label-primary, #111);
  margin-bottom: 2px;
}

.simple-item-row {
  font-size: 14px;
  color: var(--label-secondary, #555);
  line-height: 1.4;
}

.simple-item-row a {
  color: var(--accent, #007aff);
  margin-left: 4px;
}

.simple-item-obs {
  font-style: italic;
  margin-top: 4px;
}

.simple-edit-btn {
  flex-shrink: 0;
  border: none;
  border-radius: 10px;
  background: rgba(0, 122, 255, 0.1);
  color: var(--accent, #007aff);
  font-size: 14px;
  font-weight: 600;
  padding: 8px 12px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  -webkit-tap-highlight-color: transparent;
}

.simple-done {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 14px;
  padding: 24px;
  border-radius: 16px;
  background: var(--success-soft, #e3f7e8);
  color: var(--success-deep, #1e7d34);
  font-size: 18px;
  font-weight: 600;
  max-width: 420px;
}

.simple-done .pi {
  font-size: 2.4rem;
}

.simple-form {
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 420px;
  text-align: left;
}

.simple-back {
  align-self: flex-start;
  border: none;
  background: none;
  color: var(--accent, #007aff);
  font-size: 15px;
  font-weight: 600;
  padding: 0 0 18px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  -webkit-tap-highlight-color: transparent;
}

.simple-field-label {
  font-size: 15px;
  font-weight: 600;
  color: var(--label-secondary, #666);
  margin-bottom: 8px;
}

.simple-field-label.mt {
  margin-top: 22px;
}

.simple-input {
  width: 100%;
  box-sizing: border-box;
  border-radius: 12px;
  border: 1.5px solid rgba(0, 0, 0, 0.15);
  background: var(--bg-primary, #fff);
  padding: 14px 16px;
  font-size: 17px;
  color: var(--label-primary, #111);
}

.simple-textarea {
  resize: vertical;
  font-family: inherit;
}

.simple-error-msg {
  margin-top: 16px;
  color: var(--danger, #ff3b30);
  font-size: 15px;
  text-align: center;
}

.simple-gps-btn {
  width: 100%;
  padding: 16px;
  border: none;
  border-radius: 14px;
  background: var(--accent, #007aff);
  color: #fff;
  font-size: 16px;
  font-weight: 700;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  -webkit-tap-highlight-color: transparent;
}

.simple-gps-btn:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.simple-map-btn {
  margin-top: 10px;
  background: rgba(0, 122, 255, 0.12);
  color: var(--accent, #007aff);
}

.simple-map {
  width: 100%;
  height: 280px;
  margin-top: 10px;
  border-radius: 12px;
  overflow: hidden;
}

.simple-optional {
  font-weight: 400;
  color: var(--label-tertiary, #999);
}

.simple-help {
  font-size: 13px;
  color: var(--label-secondary, #777);
  margin-bottom: 8px;
  line-height: 1.4;
}

.simple-gps-status {
  margin-top: 10px;
  font-size: 14px;
  color: var(--success-deep, #1e7d34);
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
}

.simple-gps-status a {
  color: inherit;
  text-decoration: underline;
}

.simple-link-row {
  display: flex;
  gap: 8px;
  align-items: center;
}

.simple-link-row .simple-input {
  flex: 1;
}

.simple-link-btn {
  flex-shrink: 0;
  width: 48px;
  height: 48px;
  border: none;
  border-radius: 12px;
  background: rgba(0, 122, 255, 0.12);
  color: var(--accent, #007aff);
  font-size: 18px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  -webkit-tap-highlight-color: transparent;
}

.simple-link-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.simple-btn {
  margin-top: 28px;
  width: 100%;
  padding: 18px;
  border: none;
  border-radius: 14px;
  background: var(--success, #34c759);
  color: #fff;
  font-size: 19px;
  font-weight: 700;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  -webkit-tap-highlight-color: transparent;
}

.simple-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.simple-btn-add {
  max-width: 420px;
  margin-top: 4px;
}

.simple-btn-secondary {
  margin-top: 4px;
  background: rgba(0, 0, 0, 0.12);
  color: var(--label-primary, #111);
}

.simple-adjuntos-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 8px;
}

.simple-adjunto-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  background: var(--bg-primary, #fff);
  border: 1.5px solid rgba(0, 0, 0, 0.1);
  border-radius: 10px;
  padding: 8px 10px;
}

.simple-adjunto-link {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
  color: var(--label-primary, #111);
  text-decoration: none;
}

.simple-adjunto-thumb {
  width: 36px;
  height: 36px;
  border-radius: 8px;
  object-fit: cover;
  flex-shrink: 0;
}

.simple-adjunto-icon {
  font-size: 20px;
  color: var(--label-secondary, #666);
  flex-shrink: 0;
}

.simple-adjunto-name {
  font-size: 14px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.simple-adjunto-remove {
  flex-shrink: 0;
  border: none;
  background: rgba(255, 59, 48, 0.1);
  color: var(--danger, #ff3b30);
  width: 28px;
  height: 28px;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  -webkit-tap-highlight-color: transparent;
}

.simple-adjunto-remove:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.simple-file-btn {
  margin-top: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 14px;
  border-radius: 12px;
  border: 1.5px dashed rgba(0, 0, 0, 0.25);
  color: var(--accent, #007aff);
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}

.simple-file-input {
  display: none;
}

.simple-item-adjuntos {
  display: flex;
  gap: 6px;
  margin-top: 6px;
  flex-wrap: wrap;
}

.simple-item-adjuntos a {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border-radius: 8px;
  overflow: hidden;
  background: var(--bg-grouped, #f2f2f7);
  color: var(--label-secondary, #666);
}

.simple-item-adjuntos img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
</style>
